import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IQuestionnaire } from '../shared/models/operation.model';
import { ICountriesObject, IPolicyAsset, IProvinceObject } from '../shared/models/policy.model';
import { EProductType, ICheckConsultancyResponse, mock_frequencies, MULTISTRATEGY_PRODUCTS } from './athora.constants';

@Injectable({
  providedIn: 'root'
})
export class AthoraService {

  private _downloadDocRetry$: Subject<boolean> = new Subject<boolean>();
  private _retrieveProvices$: Subject<void> = new Subject<void>();
  private _retrieveCities$: Subject<string> = new Subject<string>();

  constructor(
    private readonly _http: HttpClient
  ) { }

  get isLocalEnv(): boolean {
    return environment.name === 'LOCAL' || environment.name === 'LOCAL_BE';
  }

  get downloadDocRetrySubscription(): Observable<boolean> {
    return this._downloadDocRetry$.asObservable();
  }

  get retrieveProvices(): Observable<void> {
    return this._retrieveProvices$.asObservable();
  }

  get retrievePlaces(): Observable<string> {
    return this._retrieveCities$.asObservable();
  }

  downloadDocRetry(value: boolean): void {
    this._downloadDocRetry$.next(value);
  }

  retrieveProvicesList(): void {
    this._retrieveProvices$.next();
  }

  retrieveCitiesList(value: string): void {
    this._retrieveCities$.next(value);
  }

  confirmOperation(body: any, operationId: string): Observable<any> {
    const url = environment.apiBaseUrl + environment.apiPaths.athora[operationId];
    if (this.isLocalEnv) {
      return this._http.get(url);
    }
    return this._http.post<any>(url, body);
  }

  getQuestionnaire(policyId: string, operationId: string): Observable<IQuestionnaire[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.questionnaire + `?policyId=${policyId}&operationCode=${operationId}`;
    if (this.isLocalEnv) {
      return this._http.get<IQuestionnaire[]>(`/mock/getQuestionnaires`);
    }
    return this._http.get<IQuestionnaire[]>(url);
  }

  retrieveFrequencies(operationId: string, productCode: string): Observable<string[]> {
    if (this.isLocalEnv) {
      return of(mock_frequencies);
    }
    const url = environment.apiBaseUrl + environment.apiPaths.frequencies;
    const params = new HttpParams()
      .set('productCode', productCode)
      .set('operationCode', operationId);
    return this._http.get<any>(url, {
      params: params
    });
  }

  describeOperation(operationCode: string, productCode: string,currentRisk: string,  lineCode: string): Observable<IPolicyAsset[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.describeOperation;
    if (this.isLocalEnv) {
      return this._http.get<IPolicyAsset[]>(url);
    }
    let params = new HttpParams()
      .set('operationCode', operationCode)
      .set('productCode', productCode);
    if (lineCode && !['_SWITR', '_SWITC'].includes(operationCode) && !MULTISTRATEGY_PRODUCTS.includes(EProductType[productCode])) {
      params = params.set('lineCode', lineCode);
    }
    if (currentRisk && !['_SWITR', '_SWITC'].includes(operationCode)) {
      params = params.set('fundCode', currentRisk);
    }
    return this._http.get<IPolicyAsset[]>(url, {
      params: params
    });
  }

  checkAuthCode(code: string, operationId: string, contractNumber: string): Observable<any> {
    const url = environment.apiBaseUrl + environment.apiPaths.checkAuthCode;
    return this._http.post<any>(url, {
      code: code,
      operationType: operationId,
      contractNumber: contractNumber
    })
  }

  checkConsultancy(policyNumber: string, operationCode: string, importo: string, ndgTutoreODelegato: string, codiceLineaCompagnia:string[], controvaloreLineaCompagnia:string[]): Observable<ICheckConsultancyResponse> {
    const url = environment.apiBaseUrl + environment.apiPaths.checkConsultancy;
    if (this.isLocalEnv) return this._http.get<any>(url);

    return this._http.post<ICheckConsultancyResponse>(url, { policyNumber, operationCode, importo, codiceLineaCompagnia, controvaloreLineaCompagnia, ndgTutoreODelegato }, {})
  }

  retrieveCountries(): Observable<ICountriesObject[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.retrieveCountries;
    return this._http.get<ICountriesObject[]>(url);
  }

  retrieveProvinces(): Observable<IProvinceObject[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.retrieveProvinces;
    return this._http.get<IProvinceObject[]>(url);
  }

}
